import { User, UserTypes } from "contexts/types/application-context-types";
import { PermissionRule, DefaultRulesRepository } from "./permission-rules";

export class Route {
  private component: () => JSX.Element;
  private path: string;
  private permission: UserTypes[] = [];
  private colomboPermission: UserTypes[] = [];
  private customPermissions: ((user: User | undefined) => boolean)[] = [];
  private globalAccessRules = DefaultRulesRepository.getInstance();

  constructor(path: string, component: () => JSX.Element) {
    this.component = component;
    this.path = path;
  }

  /**
   * Define os tipos de usuários permitidos para acessar esta rota
   */
  public setUserTypesPermission(userTypes: UserTypes[]) {
    this.permission = userTypes;
  }

  /**
   * Define os tipos de usuários Colombo permitidos para acessar esta rota
   */
  public setColomboUserTypesPermission(userTypes: UserTypes[]) {
    this.colomboPermission = userTypes;
  }

  /**
   * Adiciona uma função customizada para validar permissões específicas
   */
  public addCustomPermission(fn: (user: User | undefined) => boolean) {
    this.customPermissions.push(fn);
  }

  /**
   * Adiciona uma nova regra de permissão global (para esta rota)
   * Permite extensibilidade sem modificar o código existente
   */
  public addDefaultRule(rule: PermissionRule) {
    this.globalAccessRules.addRule(rule);
  }

  /**
   * Remove uma regra de permissão global
   */
  public removeDefaultRule(ruleName: string) {
    this.globalAccessRules.removeRule(ruleName);
  }

  /**
   * Verifica se o usuário passou em todas as validações customizadas
   */
  private checkCustomPermissions(user: User | undefined) {
    return this.customPermissions.every((fn) => fn(user));
  }

  /**
   * Verifica se um usuário Colombo tem permissão para acessar esta rota
   * Retorna false por padrão para ocultar rotas de usuários Colombo
   */
  private checkColomboPermitions(user: User | undefined) {
    if (!this.colomboPermission.length) return false; // Por padrão as rotas são escondidas para usuarios Colombo
    return !!this.colomboPermission.includes(user?.userData.type!);
  }

  /**
   * Verifica se o tipo de usuário tem permissão para acessar esta rota
   */
  private checkUserTypePermissions(user: User | undefined) {
    if (!this.permission.length) return true;
    return this.permission.includes(user?.userData.type!) ?? false;
  }

  /**
   * Valida as regras que valem para todas as páginas.
   */
  private validateGlobalAccessRules(user: User | undefined) {
    return this.globalAccessRules.validateAll(user, this.path);
  }

  /**
   * Executa todas as validações de permissão e retorna se o usuário pode acessar a rota
   * Verifica: tipos de usuário, permissões customizadas, usuários Colombo e regras globais
   */
  public checkPermission(user: User | undefined) {
    const hasGlobalAccess = this.validateGlobalAccessRules(user);

    if (!hasGlobalAccess) return false;

    if (
      !this.customPermissions.length &&
      !this.permission.length &&
      !this.colomboPermission.length
    ) {
      return true;
    }

    const isColomboUser =
      !!user?.userData.customerCodigoNova?.startsWith("1309");
    const hasCustomPermission = this.checkCustomPermissions(user);

    if (!this.permission.length && !this.colomboPermission.length) {
      return hasCustomPermission;
    }

    if (isColomboUser) {
      return this.checkColomboPermitions(user) && hasCustomPermission;
    }

    return this.checkUserTypePermissions(user) && hasCustomPermission;
  }

  /**
   * Retorna o caminho (path) desta rota
   */
  public getPath() {
    return this.path;
  }

  /**
   * Retorna o componente React associado a esta rota
   */
  public getComponent() {
    return this.component;
  }
}

/**
 * Valida se um usuário tem permissão com base em seu tipo
 * Usuários Colombo usam validação específica se fornecida, caso contrário usa validação padrão
 */
export function checkPermission(
  user: User | undefined,
  validUserTypes: UserTypes[],
  colomboValidUserTypes?: UserTypes[]
) {
  const isColomboUser = user?.userData.customerCodigoNova?.startsWith("1309");

  if (isColomboUser)
    return !!colomboValidUserTypes?.includes(user?.userData.type!);

  return validUserTypes.includes(user?.userData.type!);
}
